/**
 * ValueChainWheel — Round 2 P1 (Homepage item 5).
 *
 * Redesigns the plain value-chain pill strip into a proper visual sequence
 * of 8 nodes: Farmer → Soil → Farm → Produce → Procurement → Traceability
 * → Market Linkage → Consumer. Sequence and content are locked by the brief;
 * this component only owns the visual presentation.
 */
import { useTranslation } from "react-i18next";
import { User, Sprout, TreePine, Package2, Handshake, ScanLine, Truck, ShoppingBag } from "lucide-react";

const STEPS = [
  { key: "vc_1", icon: User },
  { key: "vc_2", icon: Sprout },
  { key: "vc_3", icon: TreePine },
  { key: "vc_4", icon: Package2 },
  { key: "vc_5", icon: Handshake },
  { key: "vc_6", icon: ScanLine },
  { key: "vc_7", icon: Truck },
  { key: "vc_8", icon: ShoppingBag },
];

export default function ValueChainWheel() {
  const { t } = useTranslation();
  return (
    <section
      data-testid="value-chain-wheel"
      className="relative bg-[var(--tr-deep-green)] text-white tr-grain overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="max-w-3xl">
          <div className="tr-eyebrow !text-[var(--tr-citrus-yellow)] before:!bg-[var(--tr-citrus-yellow)]">
            {t("home.value_chain_eyebrow")}
          </div>
          <h2 className="mt-4 font-display font-extrabold text-3xl md:text-5xl leading-[0.95] tracking-tight">
            {t("home.value_chain_title")}
          </h2>
          <p className="mt-5 text-white/75 leading-relaxed">
            {t("home.value_chain_body")}
          </p>
        </div>

        {/* Nodes — 8 steps in a 4-column grid that wraps on mobile.
            Each node is a numbered circle icon with a label; consecutive
            nodes are connected by a dashed horizontal line on desktop. */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-y-14 gap-x-6 relative">
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              data-testid={`vcw-node-${i + 1}`}
              className="relative flex flex-col items-center text-center"
            >
              <div className="relative">
                <span className="absolute -top-3 -right-3 z-10 w-8 h-8 rounded-full bg-[var(--tr-citrus-yellow)] text-[var(--tr-deep-green)] grid place-items-center font-display font-extrabold text-xs shadow-lg">
                  {i + 1}
                </span>
                <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-white/[0.06] border border-white/25 grid place-items-center backdrop-blur transition-transform hover:scale-105">
                  <s.icon className="w-9 h-9 md:w-11 md:h-11 text-[var(--tr-citrus-yellow)]" strokeWidth={1.5} />
                </div>
              </div>
              <div className="mt-5 font-display font-extrabold text-lg md:text-xl leading-tight">
                {t(`home.${s.key}`)}
              </div>
              <div className="mt-2 text-xs text-white/60 leading-relaxed max-w-[180px]">
                {t(`home.vcw_${s.key}_d`)}
              </div>

              {/* Right connector — visible on md+ except last-in-row (index 3, 7) and last item */}
              {i !== STEPS.length - 1 && (i + 1) % 4 !== 0 && (
                <span
                  aria-hidden
                  className="hidden md:block absolute top-12 md:top-14 -right-6 lg:-right-3 w-12 lg:w-6 h-px border-t border-dashed border-white/30"
                />
              )}
              {/* Down connector — end of first row on md+ */}
              {(i + 1) === 4 && (
                <span
                  aria-hidden
                  className="hidden md:block absolute -bottom-8 left-1/2 w-px h-8 border-l border-dashed border-white/30"
                />
              )}
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-4 text-sm text-white/70 max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em]">
            {t("home.value_chain_pill")}
          </span>
          <span className="italic">{t("home.value_chain_footer")}</span>
        </div>
      </div>
    </section>
  );
}
